import React, {useCallback, useMemo, useReducer, useState} from 'react';
import {toast} from 'react-toastify';
import {getGetMeshesQueryKey, useAddMeshesToProject, useGetMeshes, type MeshType} from 'api/generated';
import {ModelItem, Button, ModalModelAdd, TextWithLine, Transitioner, Modal, ModelViewer, ModalModelUpdate} from 'components';
import {useMe} from 'hooks';
import {produce} from 'immer';
import {queryClient} from 'App';

import useProjectStore from '../../stores/useProjectStore';

interface ISidebarLeftAssetsProps {}

const SidebarLeftAssets = ({}: ISidebarLeftAssetsProps) => {
  const projectUid = useProjectStore(s => s.uid);
  const meshes = useProjectStore(s => s.meshes) ?? [];
  const addMeshes = useProjectStore(s => s.addMeshes);
  const assignMeshes = useProjectStore(s => s.assignMeshes);

  const [showAdd, toggleAdd] = useReducer(s => !s, false);
  const [showUpdate, toggleUpdate] = useReducer(s => !s, false);
  const [showModel, toggleModel] = useReducer(s => !s, false);
  const [toAssign, setToAssign] = useState<MeshType[]>([]);
  const [toShow, setToShow] = useState<MeshType>();

  const me = useMe();
  const users = useProjectStore(s => s.users) ?? [];

  const projectRole = useMemo(() => {
    const user = users.find(u => u.uid === me?.uid);
    return user?.projectRole;
  }, [me, users]);

  const filter = projectRole === 'creator' ? 'mine' : 'collab';
  const {data: meshesData} = useGetMeshes({filter}, {query: {staleTime: 10 * 1000}});
  const myMeshes = meshesData?.data?.data ?? [];

  const handleUploaded = useCallback((mesh: MeshType) => {
    queryClient.invalidateQueries({queryKey: [getGetMeshesQueryKey({filter: 'mine'})[0]]});
    setToShow(mesh);
    toggleUpdate();
  }, []);

  const addMeshesToProject = useAddMeshesToProject();
  const handleAddToProject = useCallback(async (meshes: MeshType[]) => {
    const {data: res} = await addMeshesToProject.mutateAsync({
      data: {
        projectUid,
        uids: meshes.map(m => m.uid),
      },
    });
    if (res.data?.success) {
      toast.success(res.data.message);
      toggleAdd();
      addMeshes(meshes);
    } else if (res.data?.message) {
      toast.error(res.data.message);
    } else if (res.errors) {
      toast.error(res.errors[0].detail);
    }
  }, []);

  const handleAssignToProject = () => {
    assignMeshes(toAssign);
    setToAssign([]);
  };

  const handleClicked = useCallback((mesh: MeshType) => {
    setToAssign(
      produce(draft => {
        const index = draft.findIndex(m => m.uid === mesh.uid);
        if (index === -1) {
          draft.unshift(mesh);
        } else {
          draft.splice(index, 1);
        }
      }),
    );
  }, []);

  const handleShow = useCallback((mesh: MeshType) => {
    setToShow(mesh);
    toggleModel();
  }, []);

  const unassignedMeshes = useMemo(() => {
    return meshes.filter(m => !m.assigned);
  }, [meshes]);

  const assignedMeshes = useMemo(() => {
    return meshes.filter(m => m.assigned);
  }, [meshes]);

  const addedMeshes = useMemo(() => {
    return [...unassignedMeshes, ...assignedMeshes];
  }, [unassignedMeshes, assignedMeshes]);

  return (
    <Transitioner fromY={-20} delay={0.1}>
      <div className="flex justify-between items-center">
        <h2 className="dark:text-white">Models</h2>
        <Button text="+ New model" size="extra-small" onClick={toggleAdd} />
      </div>

      <div className="my-3">
        <TextWithLine text="" />
      </div>

      <div className="flex items-center justify-between">
        {toAssign.length === 0 ? <span className="text-sm/6">Files</span> : <span className="text-sm/6">{toAssign.length} selected</span>}
        <div className="w-28.5 flex flex-col whitespace-nowrap">
          <Button text="Assign models" size="extra-small" variant="secondary" onClick={handleAssignToProject} disabled={toAssign.length === 0} />
        </div>
      </div>
      <p className="mt-3 mb-2 text-xs text-slate-500 dark:text-darkText">Select models to assign to this project.</p>

      <ModalModelAdd
        visible={showAdd}
        onClose={toggleAdd}
        onConfirm={handleAddToProject}
        onUploaded={handleUploaded}
        addedMeshes={addedMeshes}
        meshes={myMeshes}
        projectUid={projectUid}
      />

      <ModalModelUpdate visible={showUpdate} onClose={toggleUpdate} mesh={toShow} />

      <Modal visible={showModel} onClose={toggleModel}>
        {!!toShow && <ModelViewer mesh={toShow} />}
      </Modal>

      <Transitioner fromY={-15} delay={0.2} classNames="py-3">
        <ul className="mt-2 -m-1.5 grid grid-cols-2 gap-y-1">
          {unassignedMeshes.map(mesh => {
            const checked = !!toAssign.find(s => s.uid === mesh.uid);
            return <ModelItem key={mesh.uid} mesh={mesh} onClick={handleClicked} onShow={handleShow} readOnly checked={checked} />;
          })}
        </ul>
      </Transitioner>
    </Transitioner>
  );
};

export default React.memo(SidebarLeftAssets);
